import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Stack,
} from '@mui/material';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

import type { GetPropertyTypesQuery } from '@graphql/__generated__/graphql';
import { GET_PROPERTY_TYPES } from '@modules/_shared/common/queries';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import { isPropertyTypeInCompositeGroup } from '@modules/_shared/utils/propertyTypeHelpers';
import { useLocale } from '@shared/hooks/locale';

type PropertyType = GetPropertyTypesQuery['property_types'][number];

export type PropertyTypesTreeProps = {
  values: PropertyType[];
  onChange: (values: PropertyType[]) => void;
  validationError?: string;
};

const sortByLabelOrName = (a: PropertyType, b: PropertyType) =>
  (a.label ?? a.name ?? '').localeCompare(b.label ?? b.name ?? '');

export const PropertyTypesTree: React.FC<PropertyTypesTreeProps> = ({
  values,
  onChange,
  validationError,
}) => {
  const { t } = useLocale();
  const { data, loading, error } = useQuery(GET_PROPERTY_TYPES);

  const propertyTypesMap = useMemo(
    () =>
      data?.property_types.reduce((acc, propertyType) => {
        if (propertyType.type !== 'simple') {
          return acc;
        }

        const key = propertyType.composite_type_object?.id ?? 'other';

        acc[key] = [...(acc[key] ?? []), propertyType];
        return acc;
      }, {} as Record<string, PropertyType[]>) ?? {},
    [data],
  );

  if (loading) {
    // Create skeleton
    return null;
  }

  if (error || data == null) {
    return <Alert severity="error">{t('Failed to load property types')}</Alert>;
  }

  const orderedCompositeTypes = data.property_types
    .filter(propertyType => propertyType.type === 'composite')
    .sort(sortByLabelOrName);

  return (
    <FormControl component="fieldset" variant="standard" error={!!error}>
      <RaLabel label={t('Property types')} style={{ fontWeight: 500 }} />
      <Stack direction="column" spacing={0}>
        {orderedCompositeTypes.map(compositePropertyType => (
          <ComposedPropertyTypeCheckbox
            propertyType={compositePropertyType}
            propertyTypesMap={propertyTypesMap}
            selectedValues={values}
            onChange={onChange}
          />
        ))}
        <RaLabel
          label={t('Other')}
          component="legend"
          style={{ fontWeight: 500, marginTop: '8px', marginLeft: '8px' }}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: 'wrap',
            ml: 3,
          }}
        >
          {propertyTypesMap.other.sort(sortByLabelOrName).map(propertyType => (
            <FormControlLabel
              label={propertyType.label ?? propertyType.name}
              sx={{ ml: '8px !important' }}
              key={propertyType.id}
              control={
                <Checkbox
                  checked={values.some(
                    selectedPropertyType =>
                      selectedPropertyType.id === propertyType.id,
                  )}
                  sx={{ p: '6px' }}
                  value={propertyType.id}
                  onChange={(_, checked) => {
                    onChange(
                      checked
                        ? [...values, propertyType]
                        : values.filter(
                            selectedPropertyType =>
                              selectedPropertyType.id !== propertyType.id,
                          ),
                    );
                  }}
                />
              }
            />
          ))}
        </Stack>
      </Stack>
      <FormHelperText>{validationError}</FormHelperText>
    </FormControl>
  );
};

const ComposedPropertyTypeCheckbox: React.FC<{
  propertyType: PropertyType;
  propertyTypesMap: Record<string, PropertyType[]>;
  selectedValues: PropertyType[];
  onChange: (values: PropertyType[]) => void;
}> = ({ propertyType, propertyTypesMap, selectedValues, onChange }) => {
  const childrenPropertyTypes = [...propertyTypesMap[propertyType.id]].sort(
    sortByLabelOrName,
  );

  const hasChildrenSelected = useMemo(
    () =>
      selectedValues.some(
        selectedPropertyType =>
          selectedPropertyType.type !== 'composite' &&
          isPropertyTypeInCompositeGroup(
            selectedPropertyType,
            propertyType.name,
          ),
      ),
    [selectedValues, propertyType],
  );

  const [expanded, setExpanded] = useState(hasChildrenSelected);

  const compositeTypeChecked = useMemo(
    () =>
      selectedValues.some(
        selectedPropertyType => selectedPropertyType.id === propertyType.id,
      ),
    [selectedValues, propertyType],
  );

  return (
    <Box key={propertyType.id}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
        }}
      >
        <IconButton
          onClick={() => setExpanded(!expanded)}
          size="small"
          disableRipple
        >
          {expanded ? (
            <ChevronDownIcon size={18} />
          ) : (
            <ChevronRightIcon size={18} />
          )}
        </IconButton>

        <FormControlLabel
          label={propertyType.label ?? propertyType.name}
          sx={{ ml: 0 }}
          control={
            <Checkbox
              checked={compositeTypeChecked}
              indeterminate={hasChildrenSelected}
              value={propertyType.id}
              sx={{ p: '6px' }}
              onChange={(_, checked) => {
                // If some children are selected, clicking on the composite type should unselect it.
                const newChecked = hasChildrenSelected ? false : checked;
                // Remove all children of the composite type, because it is either selected (so all children values are set) or deselected (so all children values are removed)
                const newValue = selectedValues.filter(
                  selectedPropertyType =>
                    selectedPropertyType.type === 'composite' ||
                    !isPropertyTypeInCompositeGroup(
                      selectedPropertyType,
                      propertyType.name,
                    ),
                );

                onChange(
                  newChecked
                    ? [...newValue, propertyType]
                    : newValue.filter(
                        selectedPropertyType =>
                          selectedPropertyType.id !== propertyType.id,
                      ),
                );
              }}
            />
          }
        />
      </Stack>
      <Collapse in={expanded}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: 'wrap',
            ml: 4,
          }}
        >
          {childrenPropertyTypes.map(childPropertyType => {
            const isChildSelected = selectedValues.some(
              selectedPropertyType =>
                selectedPropertyType.id === childPropertyType.id,
            );

            const isParentSelected = selectedValues.some(
              selectedPropertyType =>
                selectedPropertyType.id === propertyType.id,
            );

            return (
              <FormControlLabel
                label={childPropertyType.label ?? childPropertyType.name}
                sx={{ ml: '8px !important' }}
                key={childPropertyType.id}
                control={
                  <Checkbox
                    checked={isChildSelected || isParentSelected}
                    sx={{ p: '6px' }}
                    value={childPropertyType.id}
                    onChange={(_, checked) => {
                      if (!checked) {
                        let newValues = selectedValues;

                        if (isParentSelected) {
                          // If we unselect but the parent type was previsouly selected, it means that we need to remove it and add all other children values.
                          newValues = [
                            ...newValues.filter(
                              selectedPropertyType =>
                                selectedPropertyType.id !== propertyType.id,
                            ),
                            ...childrenPropertyTypes,
                          ];
                        }

                        onChange(
                          newValues.filter(
                            pt => pt.id !== childPropertyType.id,
                          ),
                        );
                      } else {
                        let newValues = [...selectedValues, childPropertyType];

                        if (
                          childrenPropertyTypes.every(pt =>
                            newValues.some(
                              selectedPropertyType =>
                                selectedPropertyType.id === pt.id,
                            ),
                          )
                        ) {
                          // If all children are selected, we remove all and add the parent type.
                          newValues = [
                            ...newValues.filter(
                              pt =>
                                !isPropertyTypeInCompositeGroup(
                                  pt,
                                  propertyType.name,
                                ),
                            ),
                            propertyType,
                          ];
                        }

                        onChange(newValues);
                      }
                    }}
                  />
                }
              />
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
};
